import React, { useState, useEffect, useMemo } from "react";
import {
  Add_TestSettings,
  Get_Categorry,
  Get_SubCategory,
  GetDataBySearch,
  ToggleFavoriteCategory,
  ToggleFavoriteSubCategory,
  ToggleFavoriteTest,
  GetStaticResult,
  AddStaticResult,
  DeleteStaticResult,
} from "../Services/TestServices";
import { toast } from "react-toastify";
import { Button } from "@mui/material";
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import { useApi } from "../ContextApi/ContextApi";
import '../UI/TestSettings.css';

const TestSettings = ({ onSaved }) => {
  const { setTest_SubmissionStatus } = useApi();
  const LabID = sessionStorage.getItem('LabID');
  const [formData, setFormData] = useState({
    CataGory_Value: '',
    sub_Categorry: '',
    Age_Value: '',
    LabID: sessionStorage.getItem('LabID'),
  });

  // ----------- Result suggestions (per test / lab-wide) -----------
  const [staticResults, setStaticResults] = useState([]);
  // '' => lab-wide suggestion (shows on every test); otherwise a testID string.
  const [suggestionTestId, setSuggestionTestId] = useState('');
  const [suggestionText, setSuggestionText] = useState('');
  const [savingSuggestion, setSavingSuggestion] = useState(false);

  // ----------- Favorite tags (Category / Sub Category / Test) -----------
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [tests, setTests] = useState([]);
  const [testFilter, setTestFilter] = useState('');

  const loadFavoriteLists = async () => {
    try {
      const [categoryList, subCategoryList, testList, suggestionList] = await Promise.all([
        Get_Categorry(LabID),
        Get_SubCategory(LabID),
        GetDataBySearch({ LabID }),
        GetStaticResult(LabID),
      ]);
      setCategories(categoryList || []);
      setSubCategories(subCategoryList || []);
      setTests(testList || []);
      setStaticResults(suggestionList || []);
    } catch (error) {
      console.error("Error fetching favorite lists:", error);
    }
  };

  const reloadSuggestions = async () => {
    try {
      const suggestionList = await GetStaticResult(LabID);
      setStaticResults(suggestionList || []);
    } catch (error) {
      console.error("Error fetching result suggestions:", error);
    }
  };

  // Suggestions that apply to the currently selected scope: the picked test's
  // own suggestions, or the lab-wide ones when "All tests" is selected.
  const scopedSuggestions = useMemo(() => {
    if (!suggestionTestId) {
      return staticResults.filter((s) => s.testID === null || s.testID === undefined);
    }
    return staticResults.filter((s) => String(s.testID) === String(suggestionTestId));
  }, [staticResults, suggestionTestId]);

  const addSuggestion = async () => {
    const text = suggestionText.trim();
    if (!text) {
      toast.info("Enter a result suggestion");
      return;
    }
    const reserved = ['nill', 'positive', 'negative'];
    if (reserved.includes(text.toLowerCase())) {
      toast.info("This result is already available by default");
      return;
    }
    const duplicate = scopedSuggestions.some(
      (s) => (s.sResult || '').trim().toLowerCase() === text.toLowerCase()
    );
    if (duplicate) {
      toast.info("This suggestion is already added");
      return;
    }
    setSavingSuggestion(true);
    try {
      await AddStaticResult(LabID, text, suggestionTestId || undefined);
      toast.success("Suggestion added");
      setSuggestionText('');
      await reloadSuggestions();
    } catch (error) {
      toast.error("Failed to add suggestion");
    } finally {
      setSavingSuggestion(false);
    }
  };

  const deleteSuggestion = async (SRID) => {
    try {
      await DeleteStaticResult(LabID, SRID);
      setStaticResults((prev) => prev.filter((s) => s.srid !== SRID));
      toast.success("Suggestion removed");
    } catch (error) {
      toast.error("Failed to remove suggestion");
    }
  };

  useEffect(() => {
    loadFavoriteLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCategoryFavorite = async (item) => {
    const nextValue = !item.isFavorite;
    setCategories((prev) =>
      prev.map((c) => (c.categorY_CODE === item.categorY_CODE ? { ...c, isFavorite: nextValue } : c))
    );
    try {
      await ToggleFavoriteCategory(LabID, item.categorY_CODE, nextValue);
      toast.success(nextValue ? "Added to favorites" : "Removed from favorites");
    } catch (error) {
      toast.error("Failed to update favorite");
      setCategories((prev) =>
        prev.map((c) => (c.categorY_CODE === item.categorY_CODE ? { ...c, isFavorite: !nextValue } : c))
      );
    }
  };

  const toggleSubCategoryFavorite = async (item) => {
    const nextValue = !item.isFavorite;
    setSubCategories((prev) =>
      prev.map((s) => (s.suB_CATEGORY_CODE === item.suB_CATEGORY_CODE ? { ...s, isFavorite: nextValue } : s))
    );
    try {
      await ToggleFavoriteSubCategory(LabID, item.suB_CATEGORY_CODE, nextValue);
      toast.success(nextValue ? "Added to favorites" : "Removed from favorites");
    } catch (error) {
      toast.error("Failed to update favorite");
      setSubCategories((prev) =>
        prev.map((s) => (s.suB_CATEGORY_CODE === item.suB_CATEGORY_CODE ? { ...s, isFavorite: !nextValue } : s))
      );
    }
  };

  const toggleTestFavorite = async (item) => {
    const nextValue = !item.isFavorite;
    setTests((prev) =>
      prev.map((t) => (t.testID === item.testID ? { ...t, isFavorite: nextValue } : t))
    );
    try {
      await ToggleFavoriteTest(LabID, item.testID, nextValue);
      toast.success(nextValue ? "Added to favorites" : "Removed from favorites");
    } catch (error) {
      toast.error("Failed to update favorite");
      setTests((prev) =>
        prev.map((t) => (t.testID === item.testID ? { ...t, isFavorite: !nextValue } : t))
      );
    }
  };

  const filteredTests = useMemo(() => {
    const query = testFilter.trim().toLowerCase();
    if (!query) return tests;
    return tests.filter((t) => t.testName?.toLowerCase().includes(query));
  }, [tests, testFilter]);
  // ----------- END Favorite tags -----------

  const onChange = (e) => {
    const { value, name } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

 

 

  const save = async (e) => {
    e.preventDefault();
    try {
      if(formData.Age_Value == "" && formData.CataGory_Value=="" && formData.sub_Categorry == "")
      {
          toast.info("boxes are empty")
      }else{
        const response = await Add_TestSettings(formData);
        if (response) {
          toast.success("Data saved successfully");
          loadFavoriteLists();
          setTest_SubmissionStatus(true);
          if (onSaved) onSaved();
        } else {
          toast.error("Something went wrong");
        }
      }
     
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <>
      <div className="container-fluid" style={{ width: '100%',border:'1px solid lightgray',padding:'20px' }}>
      <h5 className="common-FontSize-small common-FontFamil">Note:You can add values individually, one at a time .</h5>
        <hr></hr>
        <div className="row">
          <div className='col-sm-4'>
            <label className='common-Labels' htmlFor="CataGory_Value">Category Value:</label>
          </div>
           <div className="col-sm-6">  
            <input
              className="form-control form-control common-Input"
              id='CataGory_Value'
              name='CataGory_Value'
              value={formData.CataGory_Value}
              onChange={onChange}
            />
          </div>
        </div>
        <div className="row">
          <div className='col-sm-4'>
            <label className='common-Labels' htmlFor="sub_Categorry">Sub Category:</label>
          </div>
          <div className="col-sm-6 mt-2"> 
            <input
              className="form-control form-control common-Input"
              id='sub_Categorry'
              name='sub_Categorry'
              value={formData.sub_Categorry}
              onChange={onChange}
            />
          </div>
          <div className='col-sm-4 mt-2'>
            <label className='common-Labels' htmlFor="Age_Value">Age Value:</label>
          </div>
          <div className="col-sm-6 mt-2">
             <input
              className="form-control form-control common-Input"
              id='Age_Value'
              name='Age_Value'
              value={formData.Age_Value}
              onChange={onChange}
            />
          </div>
        </div>
        <div className="row">
          <div className='col-sm-3 mt-4'>
            <Button onClick={save}   variant="contained"  className="common-button">Save</Button>
          </div>
        </div>

        <hr className="mt-4"></hr>
        <h5 className="common-FontSize-small common-FontFamil">
          Result Suggestions: pick a test to add suggestions that show only on that
          test's result box in the patient profile. Choose "All tests" for a
          suggestion that shows everywhere.
        </h5>

        <div className="row mt-2">
          <div className="col-sm-4 mt-2">
            <label className='common-Labels' htmlFor="suggestionTestId">Test:</label>
            <select
              id="suggestionTestId"
              className="form-control common-Input"
              value={suggestionTestId}
              onChange={(e) => setSuggestionTestId(e.target.value)}
            >
              <option value="">All tests (lab-wide)</option>
              {tests.map((t) => (
                <option key={t.testID} value={t.testID}>
                  {t.testName}
                  {t.categorY_VALUE ? ` (${t.categorY_VALUE})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="col-sm-5 mt-2">
            <label className='common-Labels' htmlFor="suggestionText">Result Suggestion:</label>
            <input
              className="form-control common-Input"
              id="suggestionText"
              value={suggestionText}
              onChange={(e) => setSuggestionText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSuggestion(); } }}
            />
          </div>

          <div className="col-sm-3 mt-2 d-flex align-items-end">
            <Button
              onClick={addSuggestion}
              disabled={savingSuggestion}
              variant="contained"
              className="common-button"
            >
              Add
            </Button>
          </div>
        </div>

        <div className="row mt-2">
          <div className="col-sm-12">
            <label className='common-Labels'>
              {suggestionTestId
                ? `Suggestions for ${tests.find((t) => String(t.testID) === String(suggestionTestId))?.testName || 'this test'}`
                : 'Lab-wide suggestions (shown on every test)'}
            </label>
            <div className="favorite-list-box">
              {scopedSuggestions.length === 0 && (
                <div className="common-FontSize-small text-muted p-2">No suggestions yet</div>
              )}
              {scopedSuggestions.map((item) => (
                <div key={item.srid} className="favorite-list-row">
                  <span className="common-FontSize-small">{item.sResult}</span>
                  <button
                    type="button"
                    className="favorite-star-button"
                    onClick={() => deleteSuggestion(item.srid)}
                    title="Remove suggestion"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <hr className="mt-4"></hr>
        <h5 className="common-FontSize-small common-FontFamil">Mark Favorites: shown as quick tags on the Lab Order search screen.</h5>

        <div className="row mt-2">
          <div className='col-sm-6'>
            <label className='common-Labels'>Main Categories</label>
            <div className="favorite-list-box">
              {categories.length === 0 && (
                <div className="common-FontSize-small text-muted p-2">No categories yet</div>
              )}
              {categories.map((item) => (
                <div key={item.categorY_CODE} className="favorite-list-row">
                  <span className="common-FontSize-small">{item.categorY_VALUE}</span>
                  <button
                    type="button"
                    className="favorite-star-button"
                    onClick={() => toggleCategoryFavorite(item)}
                    title={item.isFavorite ? "Remove from favorites" : "Mark as favorite"}
                  >
                    {item.isFavorite ? (
                      <StarRoundedIcon style={{ color: '#f5b301' }} />
                    ) : (
                      <StarBorderRoundedIcon style={{ color: '#999' }} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className='col-sm-6'>
            <label className='common-Labels'>Sub Categories</label>
            <div className="favorite-list-box">
              {subCategories.length === 0 && (
                <div className="common-FontSize-small text-muted p-2">No sub categories yet</div>
              )}
              {subCategories.map((item) => (
                <div key={item.suB_CATEGORY_CODE} className="favorite-list-row">
                  <span className="common-FontSize-small">{item.suB_CATEGORY_VALUE}</span>
                  <button
                    type="button"
                    className="favorite-star-button"
                    onClick={() => toggleSubCategoryFavorite(item)}
                    title={item.isFavorite ? "Remove from favorites" : "Mark as favorite"}
                  >
                    {item.isFavorite ? (
                      <StarRoundedIcon style={{ color: '#f5b301' }} />
                    ) : (
                      <StarBorderRoundedIcon style={{ color: '#999' }} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-sm-12">
            <label className='common-Labels'>Tests</label>
            <input
              className="form-control form-control common-Input mb-2"
              placeholder="Filter tests..."
              value={testFilter}
              onChange={(e) => setTestFilter(e.target.value)}
            />
            <div className="favorite-list-box favorite-list-box-tall">
              {filteredTests.length === 0 && (
                <div className="common-FontSize-small text-muted p-2">No tests found</div>
              )}
              {filteredTests.map((item) => (
                <div key={item.testID} className="favorite-list-row">
                  <span className="common-FontSize-small">
                    {item.testName}
                    {(item.categorY_VALUE || item.suB_CATEGORY_VALUE) && (
                      <span className="text-muted">
                        {" "}({item.categorY_VALUE}{item.suB_CATEGORY_VALUE ? ` / ${item.suB_CATEGORY_VALUE}` : ''})
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="favorite-star-button"
                    onClick={() => toggleTestFavorite(item)}
                    title={item.isFavorite ? "Remove from favorites" : "Mark as favorite"}
                  >
                    {item.isFavorite ? (
                      <StarRoundedIcon style={{ color: '#f5b301' }} />
                    ) : (
                      <StarBorderRoundedIcon style={{ color: '#999' }} />
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TestSettings;
